import React, { useContext } from 'react';

import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Theme,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { FormProvider } from 'react-hook-form';

import { ButtonTitleWithLoading } from '@vyce/core/src/components';
import { DeviceContext } from '@vyce/core/src/contexts';

import { Props } from './types';
import { COPY, MAIN_CONTAINER_ID } from './config';
import { useQualificationFlow } from './hooks';
import {
  CapturePhase,
  ChoosePhase,
  IntroPhase,
  ReviewPhase,
  ScanningOverlay,
  SuccessPhase,
} from './components';
import useStyles from './styles';

export const QualificationCaptureDialog = (props: Props) => {
  const classes = useStyles();
  const theme = useTheme<Theme>();
  const fullScreen = useMediaQuery(theme.breakpoints.down('xs'));
  const { isMobile } = useContext(DeviceContext);

  const {
    phase,
    isCSCS,
    photos,
    canSave,
    photosLimitReached,
    extracting,
    saving,
    methods,
    choose,
    addPhoto,
    removePhoto,
    startCapture,
    goManual,
    goToIntro,
    readPhotos,
    submit,
    addAnother,
    close,
  } = useQualificationFlow(props);

  return (
    <Dialog
      container={document.getElementById(MAIN_CONTAINER_ID)}
      fullScreen={fullScreen || isMobile}
      open={props.isOpen}
      classes={{ paper: classes.dialogPaper }}
      onClose={close}
      aria-labelledby="qualification-capture-title"
    >
      <DialogContent style={{ position: 'relative' }}>
        {extracting && <ScanningOverlay isCSCS={isCSCS} />}

        {phase === 'choose' && <ChoosePhase onChoose={choose} />}

        {phase === 'intro' && (
          <IntroPhase isCSCS={isCSCS} onStartCapture={startCapture} onManual={goManual} />
        )}

        {phase === 'capture' && (
          <CapturePhase
            isCSCS={isCSCS}
            photos={photos}
            photosLimitReached={photosLimitReached}
            onAddPhoto={addPhoto}
            onRemovePhoto={removePhoto}
            onReadPhotos={readPhotos}
          />
        )}

        <FormProvider {...methods}>
          {phase === 'review' && (
            <ReviewPhase isCSCS={isCSCS} photos={photos} onRemovePhoto={removePhoto} />
          )}
        </FormProvider>

        {phase === 'success' && (
          <SuccessPhase isCSCS={isCSCS} onAddAnother={addAnother} onDone={close} />
        )}
      </DialogContent>

      {phase === 'review' && (
        <DialogActions style={{ justifyContent: 'space-between' }}>
          <Box display="flex" gridGap={12}>
            <Button onClick={goToIntro} variant="outlined" size="large" disabled={saving}>
              {COPY.goBack}
            </Button>
            <Button onClick={close} variant="outlined" size="large" disabled={saving}>
              Close
            </Button>
          </Box>
          <Button
            onClick={submit}
            color="primary"
            variant="contained"
            size="large"
            disabled={!canSave || saving}
          >
            <ButtonTitleWithLoading
              title={COPY.review.save}
              loaderVariant="paper"
              loading={saving}
            />
          </Button>
        </DialogActions>
      )}

      {phase !== 'review' && phase !== 'success' && (
        <DialogActions>
          <Button onClick={close} variant="outlined" size="large" disabled={extracting || saving}>
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export type { Props as QualificationCaptureProps };
